import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Scales, X, MapPin, Phone, Calendar, Certificate, ArrowLeft, Printer } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useCompare } from "../context/CompareContext";
import Breadcrumb from "../components/Breadcrumb";
import { 
  getProviderName, 
  getProviderNameOnly,
  getCredentials,
  getPrimarySpecialty, 
  getFullAddress,
  getEnumerationDate,
  getNameSlug
} from "../lib/api";

const ComparePage = () => {
  const { compareList, removeFromCompare, clearCompare } = useCompare();

  if (compareList.length < 2) {
    return (
      <>
        <Helmet>
          <title>Compare Healthcare Providers | NPI Lookup</title>
          <meta name="description" content="Compare up to 3 healthcare providers side by side. View specialties, credentials, locations, and registration dates." />
        </Helmet>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={[{ name: "Home", to: "/" }, { name: "Compare Providers" }]} />
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <Scales className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">
              Select Providers to Compare
            </h1>
            <p className="text-slate-500 mb-6">
              You need at least 2 providers to compare. Browse search results and click the "Compare" button on provider cards.
            </p>
            <Link to="/">
              <Button className="bg-navy hover:bg-navy/90">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Search for Providers
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Get all taxonomies for comparison
  const getAllTaxonomies = (provider) => {
    return provider?.taxonomies?.map(t => t.desc).filter(Boolean) || [];
  };

  // Calculate years registered
  const getYearsRegistered = (provider) => {
    const enumDate = provider?.basic?.enumeration_date;
    if (!enumDate) return "N/A";
    try {
      const date = new Date(enumDate);
      const years = Math.floor((new Date() - date) / (365.25 * 24 * 60 * 60 * 1000));
      return years + (years === 1 ? " year" : " years");
    } catch {
      return "N/A";
    }
  };

  return (
    <>
      <Helmet>
        <title>{"Compare: " + compareList.map(p => getProviderNameOnly(p)).join(" vs ") + " | NPI Lookup"}</title>
        <meta name="description" content={"Side-by-side comparison of " + compareList.map(p => getProviderNameOnly(p)).join(", ") + ". Compare specialties, credentials, locations, and years registered."} />
      </Helmet>

      <div className="bg-slate-50 min-h-screen pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={[{ name: "Home", to: "/" }, { name: "Compare Providers" }]} />
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center">
                  <Scales className="w-5 h-5 text-teal" weight="fill" />
                </div>
                <h1 className="font-display font-bold text-2xl sm:text-3xl text-navy">
                  Compare Providers
                </h1>
              </div>
              <p className="text-slate-500">
                Side-by-side comparison of {compareList.length} healthcare providers
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={() => window.print()}
                variant="outline"
                className="gap-2"
                data-testid="print-compare-btn"
              >
                <Printer className="w-4 h-4" />
                Print / PDF
              </Button>
              <button
                onClick={clearCompare}
                className="text-slate-500 hover:text-slate-700 text-sm font-medium"
              >
                Clear all
              </button>
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Add More
                </Button>
              </Link>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {/* Provider Headers */}
            <div className="grid grid-cols-4 border-b border-slate-200">
              <div className="p-4 bg-slate-50 font-medium text-slate-500 text-sm">
                Attribute
              </div>
              {compareList.map((provider) => (
                <div key={provider.number} className="p-4 border-l border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <Link 
                        to={`/provider/${provider.number}/${getNameSlug(getProviderName(provider))}`}
                        className="font-display font-semibold text-navy hover:text-teal transition-colors"
                      >
                        {getProviderNameOnly(provider)}
                      </Link>
                      {getCredentials(provider) && (
                        <span className="text-slate-400 ml-1">, {getCredentials(provider)}</span>
                      )}
                      <p className="text-sm text-teal font-medium mt-1">
                        {getPrimarySpecialty(provider)}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFromCompare(provider.number)}
                      className="text-slate-400 hover:text-red-500 p-1 rounded-full hover:bg-slate-100"
                      title="Remove from comparison"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {/* Empty slots */}
              {[...Array(3 - compareList.length)].map((_, i) => (
                <div key={`empty-${i}`} className="p-4 border-l border-slate-200 bg-slate-50">
                  <div className="text-slate-400 text-sm">Empty slot</div>
                </div>
              ))}
            </div>

            {/* NPI Number */}
            <div className="grid grid-cols-4 border-b border-slate-100">
              <div className="p-4 bg-slate-50 font-medium text-slate-600 text-sm flex items-center gap-2">
                <Certificate className="w-4 h-4" />
                NPI Number
              </div>
              {compareList.map((provider) => (
                <div key={provider.number} className="p-4 border-l border-slate-100">
                  <span className="npi-badge bg-slate-100 text-slate-700 px-2 py-1 rounded font-mono text-sm">
                    {provider.number}
                  </span>
                </div>
              ))}
              {[...Array(3 - compareList.length)].map((_, i) => (
                <div key={`empty-npi-${i}`} className="p-4 border-l border-slate-100 bg-slate-50" />
              ))}
            </div>

            {/* Entity Type */}
            <div className="grid grid-cols-4 border-b border-slate-100">
              <div className="p-4 bg-slate-50 font-medium text-slate-600 text-sm">
                Entity Type
              </div>
              {compareList.map((provider) => (
                <div key={provider.number} className="p-4 border-l border-slate-100">
                  <span className={`entity-badge ${provider.enumeration_type === 'NPI-1' ? 'individual' : 'organization'}`}>
                    {provider.enumeration_type === 'NPI-1' ? 'Individual' : 'Organization'}
                  </span>
                </div>
              ))}
              {[...Array(3 - compareList.length)].map((_, i) => (
                <div key={`empty-type-${i}`} className="p-4 border-l border-slate-100 bg-slate-50" />
              ))}
            </div>

            {/* All Specialties */}
            <div className="grid grid-cols-4 border-b border-slate-100">
              <div className="p-4 bg-slate-50 font-medium text-slate-600 text-sm">
                Specialties
              </div>
              {compareList.map((provider) => (
                <div key={provider.number} className="p-4 border-l border-slate-100">
                  <ul className="space-y-1">
                    {getAllTaxonomies(provider).map((tax, i) => (
                      <li key={i} className="text-sm text-slate-700">
                        {i === 0 ? <span className="font-medium text-teal">{tax}</span> : tax}
                      </li>
                    ))}
                    {getAllTaxonomies(provider).length === 0 && (
                      <li className="text-sm text-slate-400">Not listed</li>
                    )}
                  </ul>
                </div>
              ))}
              {[...Array(3 - compareList.length)].map((_, i) => (
                <div key={`empty-spec-${i}`} className="p-4 border-l border-slate-100 bg-slate-50" />
              ))}
            </div>

            {/* Location */}
            <div className="grid grid-cols-4 border-b border-slate-100">
              <div className="p-4 bg-slate-50 font-medium text-slate-600 text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Location
              </div>
              {compareList.map((provider) => {
                const address = getFullAddress(provider);
                return (
                  <div key={provider.number} className="p-4 border-l border-slate-100">
                    {address ? (
                      <div className="text-sm">
                        <p className="text-slate-700">{address.line1}</p>
                        {address.line2 && <p className="text-slate-700">{address.line2}</p>}
                        <p className="text-slate-700">{address.city}, {address.state} {address.postalCode}</p>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">Not available</span>
                    )}
                  </div>
                );
              })}
              {[...Array(3 - compareList.length)].map((_, i) => (
                <div key={`empty-loc-${i}`} className="p-4 border-l border-slate-100 bg-slate-50" />
              ))}
            </div>

            {/* Phone */}
            <div className="grid grid-cols-4 border-b border-slate-100">
              <div className="p-4 bg-slate-50 font-medium text-slate-600 text-sm flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone
              </div>
              {compareList.map((provider) => {
                const address = getFullAddress(provider);
                return (
                  <div key={provider.number} className="p-4 border-l border-slate-100">
                    {address?.phone ? (
                      <a href={`tel:${address.phone}`} className="text-sm text-teal hover:underline">
                        {address.phone}
                      </a>
                    ) : (
                      <span className="text-sm text-slate-400">Not available</span>
                    )}
                  </div>
                );
              })}
              {[...Array(3 - compareList.length)].map((_, i) => (
                <div key={`empty-phone-${i}`} className="p-4 border-l border-slate-100 bg-slate-50" />
              ))}
            </div>

            {/* Years Registered */}
            <div className="grid grid-cols-4 border-b border-slate-100">
              <div className="p-4 bg-slate-50 font-medium text-slate-600 text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Years Registered
              </div>
              {compareList.map((provider) => (
                <div key={provider.number} className="p-4 border-l border-slate-100">
                  <span className="text-sm text-slate-700 font-medium">
                    {getYearsRegistered(provider)}
                  </span>
                  <span className="text-xs text-slate-400 block">
                    Since {getEnumerationDate(provider)}
                  </span>
                </div>
              ))}
              {[...Array(3 - compareList.length)].map((_, i) => (
                <div key={`empty-years-${i}`} className="p-4 border-l border-slate-100 bg-slate-50" />
              ))}
            </div>

            {/* Credentials */}
            <div className="grid grid-cols-4">
              <div className="p-4 bg-slate-50 font-medium text-slate-600 text-sm">
                Credentials
              </div>
              {compareList.map((provider) => (
                <div key={provider.number} className="p-4 border-l border-slate-100">
                  <span className="text-sm text-slate-700">
                    {getCredentials(provider) || <span className="text-slate-400">Not listed</span>}
                  </span>
                </div>
              ))}
              {[...Array(3 - compareList.length)].map((_, i) => (
                <div key={`empty-cred-${i}`} className="p-4 border-l border-slate-100 bg-slate-50" />
              ))}
            </div>
          </div>

          {/* View Full Profiles */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {compareList.map((provider) => (
              <Link
                key={provider.number}
                to={`/provider/${provider.number}/${getNameSlug(getProviderName(provider))}`}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:border-teal transition-colors text-center"
              >
                <span className="text-navy font-medium">View Full Profile</span>
                <span className="block text-sm text-slate-500 mt-1">{getProviderNameOnly(provider)}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ComparePage;
